import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import ScaleCard from "./ScaleCard";
import FilterControls from "./FilterControls";
import ScrollProgressBar from "./components/ScrollProgressBar";
import scalesData from "./data/scales.json";
import { applyCSSVariables } from "./lib/config";
import { useNecklaceExpansion } from "./hooks";
import "./styles.scss";

// Update meta tags for social sharing
const updateMetaTags = (scaleNumber = null, scaleData = null) => {
  const title = scaleNumber ? `Scale #${scaleNumber} - Every Scale` : 'The Encyclopedia of Scales - Every Scale';
  const description = scaleData ? 
    `Musical scale with ${scaleData.scale.length} notes (Superscale ${scaleData.superscale_index}, Mode ${scaleData.mode})` :
    'All 927 possible musical scales';
  const imageUrl = scaleNumber ? 
    `${window.location.origin}/images/${scaleNumber}.png` :
    `${window.location.origin}/img/parchment.jpg`;
  const url = window.location.href;
  
  // Update document title
  document.title = title;
  
  // Helper to update or create meta tag
  const updateMeta = (property, content, useProperty = true) => {
    const attribute = useProperty ? 'property' : 'name';
    let meta = document.querySelector(`meta[${attribute}="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attribute, property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };
  
  // OpenGraph tags
  updateMeta('og:title', title);
  updateMeta('og:description', description);
  updateMeta('og:image', imageUrl);
  updateMeta('og:url', url);
  updateMeta('og:type', 'website');
  
  // Twitter tags
  updateMeta('twitter:card', 'summary_large_image', false);
  updateMeta('twitter:title', title, false);
  updateMeta('twitter:description', description, false);
  updateMeta('twitter:image', imageUrl, false);
  
  // Standard meta description
  updateMeta('description', description, false);
};

const App = () => {
  const [allScales, setAllScales] = useState([]);
  const [filteredScales, setFilteredScales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    pattern: ""
  });
  const [activeIntervalPattern, setActiveIntervalPattern] = useState(null);
  
  // Global necklace expansion state
  const { toggleNecklace, isExpanded } = useNecklaceExpansion();

  // Initial load
  useEffect(() => {
    // Apply CSS variables from config.json
    applyCSSVariables();
    
    // Use imported data instead of fetch
    setAllScales(scalesData);
    setFilteredScales(scalesData);
    setLoading(false);
  }, []);

  // Handle hash changes (both initial load and URL changes)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#scale-')) {
        const scaleNumber = parseInt(hash.replace('#scale-', ''));
        if (!isNaN(scaleNumber) && scaleNumber >= 1 && scaleNumber <= 927) {
          // Find the scale data
          const scaleData = scalesData.find(s => s.scaleNumber === scaleNumber);
          
          // Update meta tags for social sharing
          updateMetaTags(scaleNumber, scaleData);
          
          // Wait for DOM to render, then scroll to scale and open modal
          setTimeout(() => {
            const element = document.querySelector(`[data-scale-number="${scaleNumber}"]`);
            if (element) {
              // Instant scroll (no smooth animation) for faster modal opening
              element.scrollIntoView({ behavior: 'instant', block: 'center' });
              // Open the necklace modal immediately after scroll
              setTimeout(() => toggleNecklace(scaleNumber), 50);
            }
          }, 100);
        }
      } else {
        // Reset to default meta tags for home page - no scaleNumber
        updateMetaTags();
      }
    };
    
    // Handle initial hash on load
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [toggleNecklace]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Filter scales based on pattern criteria
  useEffect(() => {
    let filtered = allScales;
    
    // Extract note count constraints (min5, max8)
    const minMatch = filters.pattern?.match(/min\[?(\d+)\]?/);
    const maxMatch = filters.pattern?.match(/max\[?(\d+)\]?/);
    const minNotes = minMatch ? parseInt(minMatch[1]) : null;
    const maxNotes = maxMatch ? parseInt(maxMatch[1]) : null;
    
    // Remove min/max from pattern for interval filtering
    const cleanPattern = filters.pattern?.replace(/min\[?\d+\]?/g, '').replace(/max\[?\d+\]?/g, '').trim() || '';

    if (filters.pattern) {

      // Apply note count filters
      if (minNotes !== null || maxNotes !== null) {
        filtered = filtered.filter(scaleData => {
          const noteCount = scaleData.scale.length;
          if (minNotes !== null && noteCount < minNotes) return false;
          if (maxNotes !== null && noteCount > maxNotes) return false;
          return true;
        });
      }

      // Apply interval pattern filters and extract patterns for highlighting
      if (cleanPattern) {
        filtered = filtered.filter(scaleData => {
          const scaleString = scaleData.scale.join("");
          
          // Support OR logic with comma separation (e.g., "12,21" = contains 12 OR 21)
          const orGroups = cleanPattern.split(',').map(p => p.trim()).filter(p => p.length > 0);
          if (orGroups.length === 0) return true;
        
        // Support AND logic with + or & (e.g., "12&^3" = contains 12 AND starts with 3)
        return orGroups.some(orGroup => {
          const andPatterns = orGroup.split(/[+&]/).map(p => p.trim()).filter(p => p.length > 0);
          
          // All AND patterns must match for this OR group to be true
          return andPatterns.every(pattern => {
            // Special filters: s5 (superscale), m3 (mode), n42 (scale number), s5m2 (both)
            if (pattern.startsWith('s') && pattern.length > 1) {
              const match = pattern.match(/^s(\d+)(?:m(\d+))?$/);
              if (match) {
                const [, superscale, mode] = match;
                const superscaleMatch = !superscale || scaleData.superscale_index === parseInt(superscale);
                const modeMatch = !mode || scaleData.mode === parseInt(mode);
                return superscaleMatch && modeMatch;
              }
            }
            
            if (pattern.startsWith('m') && pattern.length > 1) {
              const modeNum = parseInt(pattern.slice(1));
              return !isNaN(modeNum) && scaleData.mode === modeNum;
            }
            
            if (pattern.startsWith('n') && pattern.length > 1) {
              const scaleNum = parseInt(pattern.slice(1));
              return !isNaN(scaleNum) && scaleData.scaleNumber === scaleNum;
            }
            
            // Interval patterns
            if (pattern.startsWith('^')) {
              // "Starts with" pattern
              const startPattern = pattern.slice(1);
              return startPattern && scaleString.startsWith(startPattern);
            } else {
              // Contains pattern
              return pattern && scaleString.includes(pattern);
            }
          });
        });
        });
      }
    }
    
    // Extract pure interval patterns (just digits) for highlighting
    const intervalPatterns = cleanPattern
      .split(',')
      .flatMap(orGroup => orGroup.split(/[+&]/))
      .map(p => p.trim())
      .filter(p => /^\^?\d+$/.test(p)) // Only digit sequences, optionally with ^
      .map(p => p.replace(/^\^/, '')); // Remove ^ for highlighting purposes
    
    setActiveIntervalPattern(intervalPatterns.length > 0 ? intervalPatterns : null);
    setFilteredScales(filtered);
  }, [allScales, filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  if (loading) {
    return (
      <div className="app">
        <header className="app-header">
          <h1>Every Scale</h1>
          <p>Loading all 927 scales...</p>
        </header>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1 className="app-title">The Encyclopedia of Scales</h1>
        <p className="app-tagline">All 927 possible musical scales. Available for purchase.</p>
        
        <FilterControls 
          onFilterChange={handleFilterChange}
          totalScales={allScales.length}
          filteredCount={filteredScales.length}
        />
        
        <ScrollProgressBar filteredCount={filteredScales.length} />
      </header>
      
      <div className="app-content">
        <main className="scales-grid">
          {allScales.map(scaleData => {
            const isVisible = filteredScales.some(filtered => filtered.key === scaleData.key);
            return (
              <ScaleCard 
                key={scaleData.key}
                scale={scaleData.scale} 
                scaleNumber={scaleData.scaleNumber}
                scaleKey={scaleData.key}
                superscaleIndex={scaleData.superscale_index}
                mode={scaleData.mode}
                isVisible={isVisible}
                matchingPattern={activeIntervalPattern}
                onNecklaceToggle={() => toggleNecklace(scaleData.scaleNumber)}
                isNecklaceExpanded={isExpanded(scaleData.scaleNumber)}
              />
            );
          })}
        </main>
        
        {filteredScales.length === 0 && (
          <div className="no-results">
            <p>No scales match your current filters.</p>
            <p>Try adjusting the note count range or clearing the pattern search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const root = createRoot(document.getElementById("root"));
root.render(<App />);
